// src/components/classroom/ClassroomParticipantGrid.jsx
import React from "react";
import {
  useParticipants,
  useTracks,
  VideoTrack,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { FaMicrophone, FaMicrophoneSlash, FaUserGraduate, FaChalkboardTeacher } from "react-icons/fa";

export default function ClassroomParticipantGrid({ raisedHandIdentities = [] }) {
  const participants = useParticipants();

  // Query video tracks (camera and screen share)
  const cameraTracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: false },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);

  // Check if someone is screen sharing
  const screenShareTrack = cameraTracks.find(
    (t) => t.source === Track.Source.ScreenShare
  );

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden p-1.5 sm:p-4 bg-slate-950">
      {/* Screen Share Featured View if active */}
      {screenShareTrack ? (
        <div className="flex-1 flex flex-col lg:flex-row gap-3 min-h-0">
          <div className="flex-1 relative rounded-2xl overflow-hidden border border-slate-800 bg-black flex items-center justify-center shadow-xl">
            <VideoTrack
              trackRef={screenShareTrack}
              className="w-full h-full object-contain"
            />
            <div className="absolute top-3 left-3 rounded-lg bg-slate-900/80 backdrop-blur px-2.5 py-1 text-xs font-bold text-sky-400 border border-slate-700">
              Screen Share: {screenShareTrack.participant?.name || "Host"}
            </div>
          </div>

          {/* Side strip of participants */}
          <div className="lg:w-64 flex lg:flex-col gap-2 overflow-auto shrink-0 max-h-48 lg:max-h-full">
            {participants.map((p) => {
              const pTrack = cameraTracks.find(
                (t) => t.participant.identity === p.identity && t.source === Track.Source.Camera
              );
              return (
                <ParticipantTile
                  key={p.identity}
                  participant={p}
                  trackRef={pTrack}
                  isCompact={true}
                  handRaised={raisedHandIdentities.includes(p.identity)}
                />
              );
            })}
          </div>
        </div>
      ) : (
        /* Regular Dynamic Grid View */
        <div
          className={`flex-1 grid gap-3 min-h-0 ${
            participants.length <= 1
              ? "grid-cols-1"
              : participants.length === 2
              ? "grid-cols-1 landscape:grid-cols-2 md:grid-cols-2"
              : participants.length <= 4
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {participants.map((p) => {
            const pTrack = cameraTracks.find(
              (t) => t.participant.identity === p.identity && t.source === Track.Source.Camera
            );
            return (
              <ParticipantTile
                key={p.identity}
                participant={p}
                trackRef={pTrack}
                isCompact={participants.length > 4}
                handRaised={raisedHandIdentities.includes(p.identity)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ParticipantTile({ participant, trackRef, isCompact = false, handRaised = false }) {
  const isSpeaking = participant.isSpeaking;
  const isMicMuted = !participant.isMicrophoneEnabled;
  const isTeacher =
    participant.identity?.toLowerCase().includes("teacher") ||
    participant.identity?.toLowerCase().includes("host") ||
    participant.identity?.toLowerCase().includes("user:") ||
    participant.metadata?.includes("host") ||
    participant.metadata?.includes("TEACHER") ||
    participant.metadata?.includes("ADMIN");

  const displayName = participant.name || participant.identity || "Participant";
  const hasVideo = Boolean(trackRef?.publication?.isSubscribed && trackRef?.publication?.track);

  return (
    <div
      className={`relative rounded-2xl overflow-hidden border bg-slate-900 flex items-center justify-center shadow-lg transition-all ${
        isSpeaking
          ? "border-emerald-500 ring-2 ring-emerald-500/40"
          : "border-slate-800"
      }`}
    >
      {handRaised && (
        <div className="absolute right-2.5 top-2.5 z-10 flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[11px] font-black text-slate-950 shadow-lg animate-pulse">
          <FaHandPaper /> Hand raised
        </div>
      )}
      {/* Video Stream or Avatar Tile */}
      {hasVideo ? (
        <VideoTrack trackRef={trackRef} className="w-full h-full object-cover" />
      ) : (
        <div className="flex flex-col items-center justify-center p-4 text-center">
          <div
            className={`rounded-2xl flex items-center justify-center shadow-inner ${
              isCompact ? "h-12 w-12 text-xl" : "h-20 w-20 text-3xl"
            } ${
              isTeacher
                ? "bg-gradient-to-tr from-indigo-600 to-sky-600 text-white"
                : "bg-slate-800 text-slate-300 border border-slate-700"
            }`}
          >
            {isTeacher ? <FaChalkboardTeacher /> : <FaUserGraduate />}
          </div>
          <p
            className={`font-bold mt-2 text-slate-200 truncate max-w-[180px] ${
              isCompact ? "text-xs" : "text-sm"
            }`}
          >
            {displayName}
          </p>
          <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
            {isTeacher ? "Instructor" : "Student (Audio Only)"}
          </span>
        </div>
      )}

      {/* Bottom Name & Mic Pill */}
      <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-1.5 rounded-lg bg-slate-950/80 backdrop-blur px-2 py-1 text-[11px] font-semibold text-white border border-slate-800/80 truncate max-w-[75%]">
          {isTeacher && <span className="text-[9px] font-black uppercase text-indigo-400">Host</span>}
          <span className="truncate">{displayName}</span>
        </div>

        <div
          className={`h-6 w-6 rounded-lg flex items-center justify-center text-[10px] shadow backdrop-blur ${
            isMicMuted
              ? "bg-rose-950/80 text-rose-400 border border-rose-800/80"
              : isSpeaking
              ? "bg-emerald-600 text-white animate-pulse"
              : "bg-slate-950/80 text-slate-300 border border-slate-800"
          }`}
          title={isMicMuted ? "Microphone Muted" : "Microphone Active"}
        >
          {isMicMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
        </div>
      </div>
    </div>
  );
}
